import OnboardingReviewWorkspace from '../modules/talentPipeline/components/OnboardingReviewWorkspace';
import DocumentPreview from '../components/documents/DocumentPreview';
import ReuploadRequestDialog from '../components/documents/ReuploadRequestDialog';
import { loadDocumentFile } from '../services/worker/documentFile';
import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  normalizeId,
} from "../security/permissions";

import {
  finalizeFacilityMembership,
} from "../finalizeFacilityMembership";
import { saveWorkerDocumentReview, documentReviewVersion } from "../services/worker/documentReview";
import getWorkerCompliance from "../utils/compliance/getWorkerCompliance";
import getWorkerOperationalState from "../utils/workers/getWorkerOperationalState";
import DepartmentLayout from "../enterprise/layouts/DepartmentLayout";
import EnterpriseHeader from "../enterprise/components/EnterpriseHeader";
import OperationsCoach from "../enterprise/components/OperationsCoach";
import ComplianceSummary from "../modules/compliance/components/ComplianceSummary";
import ComplianceQueue from "../modules/compliance/components/ComplianceQueue";
import ComplianceWorkerCard from "../modules/compliance/components/ComplianceWorkerCard";
import ComplianceDocumentRow from "../modules/compliance/components/ComplianceDocumentRow";

const REQUIRED_DOCUMENTS = [
  ["government_id", "Government ID"],
  ["professional_license", "Professional License / Certification"],
  ["tb_test", "TB Test / Health Clearance"],
  ["cpr_bls", "CPR / BLS Certification"],
];

function cardStyle() {
  return {
    padding: 16,
    borderRadius: 20,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 30px rgba(15,23,42,.06)",
  };
}

function buttonStyle(primary = false) {
  return {
    border: primary ? "1px solid #2563eb" : "1px solid #d8e1ea",
    background: primary ? "#2563eb" : "#fff",
    color: primary ? "#fff" : "#0f172a",
    borderRadius: 10,
    padding: "8px 12px",
    fontWeight: 900,
    fontSize: 12,
    cursor: "pointer",
  };
}

export default function AdminComplianceCenter({
  users = [],
  tenantId = "",
  facilities = [],
  approvalOnly = false,
  selectedWorker = null,
  onNavigate,
}) {
  
  const [localUsers, setLocalUsers] = useState([]);
  const [busy, setBusy] = useState("");
  const [reviewedDocs, setReviewedDocs] = useState({});
  const [reviewError, setReviewError] = useState("");
  const [preview, setPreview] = useState(null);
  const [reuploadRequest, setReuploadRequest] = useState(null);
  const reviewTenantId = normalizeId(tenantId);

  function isWorkerUser(u) {
  const role = String(u?.role || "").toLowerCase();
  const platformRole = String(u?.platformRole || "").toLowerCase();
  const position = String(
    u?.position ||
      u?.primaryRole ||
      u?.workerType ||
      u?.licenseType ||
      ""
  ).toLowerCase();

  return (
    platformRole === "worker" ||
    [
      "worker",
      "cna",
      "lvn",
      "lpn",
      "rn",
      "med_aide",
      "med aide",
      "caregiver",
    ].includes(role) ||
    [
      "cna",
      "lvn",
      "lpn",
      "rn",
      "med_aide",
      "med aide",
      "caregiver",
    ].includes(position)
  );
}

function scrollToDoc(workerUid, docKey) {
  setTimeout(() => {
    const el = document.getElementById(`doc-${workerUid}-${docKey}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 100);
}

  useEffect(() => {
    setLocalUsers([]);
    setReviewedDocs({});
    setReviewError("");
    if (!reviewTenantId || reviewTenantId === "all") {
      setReviewError("Select a tenant before reviewing worker documents.");
      return;
    }
    return onSnapshot(
      query(collection(db, "users"), where("tenantId", "==", reviewTenantId)),
      (snap) => {
        setLocalUsers(snap.docs.map((d) => ({ ...d.data(), uid: d.id, id: d.id })));
        setReviewError("");
      },
      (error) => setReviewError(`Worker review updates unavailable: ${error.message}`)
    );
  }, [reviewTenantId]);

const allUsers = useMemo(() => {
  const byUid = new Map();

  // The live review subscription must win over an older parent snapshot.
  [...(users || []), ...(localUsers || [])].forEach((u) => {
    if (u.tenantId !== reviewTenantId) return;
    const key = u.uid || u.id || u.email;
    if (key) byUid.set(key, u);
  });

  return Array.from(byUid.values());
}, [localUsers, users, reviewTenantId]);

const approvedWorkers = useMemo(() => {
  return (allUsers || []).filter(
    (worker) => getWorkerOperationalState(worker).ready === true
  );
}, [allUsers]);

const reviewWorkers = useMemo(() => {
  return (allUsers || []).filter((u) => {
    if (!isWorkerUser(u)) return false;
    if (getWorkerOperationalState(u).ready === true) return false;

    const onboardingStatus = String(u.onboardingStatus || "").toLowerCase();
    const complianceStatus = String(u.complianceStatus || "").toLowerCase();
    const workerStatus = String(u.workerStatus || "").toLowerCase();
    const status = String(u.status || "").toLowerCase();

    if (
      onboardingStatus === "submitted" ||
      onboardingStatus === "pending_review" ||
      onboardingStatus === "ready_for_approval" ||
      complianceStatus === "docs_approved" ||
      workerStatus === "under_review" ||
      complianceStatus === "pending_review"
    ) {
      return true;
    }

    if (
      onboardingStatus === "action_required" ||
      onboardingStatus === "reupload_required" ||
      complianceStatus === "reupload_required" ||
      workerStatus === "action_required" ||
      status === "action_required"
    ) {
      return true;
    }

    return false;
  });
}, [allUsers]);


const actionRequired = useMemo(() => {
  return (allUsers || []).filter((u) => {
    if (!isWorkerUser(u)) return false;
    return getWorkerCompliance(u).rejectedDocs.length > 0;
  });
}, [allUsers]);

const underReview = useMemo(() => {
  return (allUsers || []).filter((u) => {
    if (!isWorkerUser(u)) return false;
    return getWorkerCompliance(u).pendingDocs.length > 0;
  });
}, [allUsers]);

const expiringSoon = useMemo(() => {
  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
  const nowMs = Date.now();

  return (allUsers || []).filter((u) => {
    if (!isWorkerUser(u)) return false;

    const docs = u.credentialMap || u.documents || u.uploadedDocs || {};

    return REQUIRED_DOCUMENTS.some(([key]) => {
      const item = docs?.[key] || {};
      const raw = item.expiresAt || item.expirationDate;

      if (!raw) return false;

      const expMs =
        raw?.toDate instanceof Function
          ? raw.toDate().getTime()
          : new Date(raw).getTime();

      return Number.isFinite(expMs) && expMs > nowMs && expMs <= nowMs + thirtyDaysMs;
    });
  });
}, [allUsers]);

  async function refreshReviewedWorker(worker) {
    try {
      const snapshot = await getDoc(doc(db, "users", worker.uid));
      if (!snapshot.exists()) throw new Error("Worker record no longer exists.");
      const freshWorker = { ...snapshot.data(), uid: snapshot.id, id: snapshot.id };
      if (freshWorker.tenantId !== worker.tenantId) throw new Error("Worker tenant changed.");
      setLocalUsers(current => [...current.filter(item => item.uid !== worker.uid), freshWorker]);
      setReviewError("");
      return freshWorker;
    } catch (error) {
      // The transaction already committed. A failed refresh must never be
      // presented as a failed approval or encourage a duplicate decision.
      setReviewError(`Document decision saved, but the worker could not be refreshed: ${error.message}. Refresh this screen before continuing.`);
      return null;
    }
  }

  async function reviewDocument(worker, docKey, status, reason = "") {
    try {
      setBusy(`${worker.uid}-${docKey}`);
      await saveWorkerDocumentReview(db, {
        worker, documentKeys: [docKey], status, reason, actor: auth.currentUser,
      });
      await refreshReviewedWorker(worker);
      return true;
    } catch (err) {
      console.error("Document review failed:", err);
      setReviewError(err?.message || "Unable to review document.");
      return false;
    } finally {
      setBusy("");
    }
  }

async function approveAllDocuments(worker) {
  try {
    setBusy(`${worker.uid}-approve-all`);

    await saveWorkerDocumentReview(db, {
      worker,
      documentKeys: REQUIRED_DOCUMENTS.map(([key]) => key),
      status: "approved",
      actor: auth.currentUser,
    });

    const freshWorker = await refreshReviewedWorker(worker);
    if (!freshWorker) return;

    const freshDocs =
      freshWorker.credentialMap ||
      freshWorker.documents ||
      freshWorker.uploadedDocs ||
      {};

    const approvedCount = REQUIRED_DOCUMENTS.filter(([key]) => {
      const item = freshDocs?.[key] || {};

      return (
        String(item.status || "").toLowerCase() === "approved" &&
        item.verified === true
      );
    }).length;

    if (approvedCount !== REQUIRED_DOCUMENTS.length) {
      throw new Error(
        `Document approval did not reconcile correctly (${approvedCount}/${REQUIRED_DOCUMENTS.length} approved).`
      );
    }

    alert(
      "All documents approved. Click Approve Worker to move this worker into Ready Workforce."
    );
  } catch (err) {
    console.error("Document approval failed:", err);
    alert(err?.message || "Unable to approve all documents.");
  } finally {
    setBusy("");
  }
}

async function approveWorkerIfReady(worker) {
  try {
    setBusy(`${worker.uid}-approve-worker`);

    const latest =
      allUsers.find((u) => u.uid === worker.uid) ||
      worker;

    const docs =
      latest.credentialMap ||
      latest.documents ||
      latest.uploadedDocs ||
      {};

    const allDocsApproved = REQUIRED_DOCUMENTS.every(([key]) => {
      const item = docs?.[key] || {};

      return (
        String(item.status || "").toLowerCase() === "approved" &&
        item.verified === true
      );
    });

    if (!allDocsApproved) {
      alert("All required documents must be approved first.");
      return;
    }

    const approvedBy =
      auth.currentUser?.email ||
      auth.currentUser?.uid ||
      "admin";

/* =====================================================
   CANONICAL WORKER TENANT
   ===================================================== */

let resolvedTenantId =
  normalizeId(
    latest?.tenantId ||
      latest?.tenantKey ||
      ""
  );

let resolvedTenantName =
  String(
    latest?.tenantName || ""
  ).trim();


/* =====================================================
   CANONICAL WORKER FACILITY SCOPE
   ===================================================== */

const resolvedFacilityIds =
  Array.from(
    new Set(
      [
        latest?.primaryFacilityId,
        latest?.facilityId,

        ...(Array.isArray(
          latest?.facilityIds
        )
          ? latest.facilityIds
          : []),

        ...(Array.isArray(
          latest?.allowedFacilityIds
        )
          ? latest.allowedFacilityIds
          : []),

        ...(Array.isArray(
          latest?.assignedFacilityIds
        )
          ? latest.assignedFacilityIds
          : []),
      ]
        .map(normalizeId)
        .filter(Boolean)
        .filter(
          (facilityId) =>
            facilityId !== "all"
        )
    )
  );

const resolvedFacilityId =
  normalizeId(
    latest?.primaryFacilityId ||
      latest?.facilityId ||
      resolvedFacilityIds[0] ||
      ""
  );

const finalFacilityIds =
  resolvedFacilityId
    ? Array.from(
        new Set([
          resolvedFacilityId,
          ...resolvedFacilityIds,
        ])
      )
    : resolvedFacilityIds;

const hasFacilityAssignment =
  Boolean(resolvedFacilityId) &&
  finalFacilityIds.length > 0;


/* =====================================================
   VALIDATE FACILITY / TENANT INTEGRITY
   ===================================================== */

let primaryFacilityData = null;

if (hasFacilityAssignment) {
  const validatedFacilities =
    await Promise.all(
      finalFacilityIds.map(
        async (facilityId) => {
          const facilitySnap =
            await getDoc(
              doc(
                db,
                "facilityProfiles",
                facilityId
              )
            );

          if (!facilitySnap.exists()) {
            throw new Error(
              `Cannot approve worker: facility profile not found for ${facilityId}.`
            );
          }

          const facilityData =
            facilitySnap.data() || {};

          const facilityTenantId =
            normalizeId(
              facilityData?.tenantId ||
                facilityData?.tenantKey ||
                ""
            );

          if (!facilityTenantId) {
            throw new Error(
              `Cannot approve worker: facility ${facilityId} is missing tenantId.`
            );
          }

          return {
            facilityId,
            facilityTenantId,
            facilityData,
          };
        }
      )
    );

  const facilityTenantIds =
    Array.from(
      new Set(
        validatedFacilities.map(
          (item) =>
            item.facilityTenantId
        )
      )
    );

  if (
    facilityTenantIds.length !== 1
  ) {
    throw new Error(
      "Cannot approve worker: assigned facilities belong to different tenants."
    );
  }

  const facilityTenantId =
    facilityTenantIds[0];

  /*
   * Existing worker tenant may not conflict with the
   * tenant owned by the assigned facilities.
   */
  if (
    resolvedTenantId &&
    resolvedTenantId !==
      facilityTenantId
  ) {
    throw new Error(
      `Cannot approve worker: worker belongs to tenant "${resolvedTenantId}" but assigned facilities belong to "${facilityTenantId}".`
    );
  }

  /*
   * Facility assignment may repair a missing worker
   * tenant, but never replace a conflicting one.
   */
  if (!resolvedTenantId) {
    resolvedTenantId =
      facilityTenantId;
  }

  primaryFacilityData =
    validatedFacilities.find(
      (item) =>
        item.facilityId ===
        resolvedFacilityId
    )?.facilityData || null;

  /*
   * tenantName remains display metadata only.
   * It is never used as authorization identity.
   */
  if (!resolvedTenantName) {
    resolvedTenantName =
      String(
        primaryFacilityData?.tenantName ||
          ""
      ).trim();
  }
}


/* =====================================================
   TENANT REMAINS REQUIRED
   ===================================================== */

/*
 * A worker may be approved without a facility,
 * but must still belong to the agency tenant.
 */
if (!resolvedTenantId) {
  throw new Error(
    "Cannot approve worker: tenantId could not be resolved."
  );
}


/* =====================================================
   APPROVE WORKER
   ===================================================== */

const approvalPayload = {
  status:
    "active",

  onboardingStatus:
    "approved",

  onboardingCompleted:
    true,

  complianceStatus:
    "approved",

  workerStatus:
    "ready",

  /*
   * READY here means the worker has completed the
   * registration/onboarding/compliance requirements.
   *
   * Operational facility access is tracked separately
   * below.
   */
  workerOperationalState:
    "READY",

  tenantId:
    resolvedTenantId,

  tenantName:
    resolvedTenantName,

  facilityAssignmentStatus:
    hasFacilityAssignment
      ? "assigned"
      : "pending",

  operationalAccessStatus:
    hasFacilityAssignment
      ? "ready"
      : "awaiting_facility",

  approvedAt:
    serverTimestamp(),

  approvedBy,

  updatedAt:
    serverTimestamp(),
};


/*
 * Do not manufacture empty facility membership for a
 * worker who has not yet been assigned anywhere.
 */
if (hasFacilityAssignment) {
  approvalPayload.facilityId =
    resolvedFacilityId;

  approvalPayload.primaryFacilityId =
    resolvedFacilityId;

  approvalPayload.facilityIds =
    finalFacilityIds;

  approvalPayload.allowedFacilityIds =
    finalFacilityIds;

  /*
   * Keep the legacy membership field synchronized
   * while older Planner/Workforce queries still use it.
   */
  approvalPayload.assignedFacilityIds =
    finalFacilityIds;

  approvalPayload.facilityName =
    latest?.facilityName ||
    primaryFacilityData?.name ||
    primaryFacilityData?.facilityName ||
    "";
}


await updateDoc(
  doc(
    db,
    "users",
    worker.uid
  ),
  approvalPayload
);


/* =====================================================
   FINALIZE OPERATIONAL ACCESS WHEN FACILITY EXISTS
   ===================================================== */

let accessFinalized = false;

if (hasFacilityAssignment) {
  await finalizeFacilityMembership({
    uid:
      worker.uid,

    email:
      latest?.email ||
      latest?.emailLower ||
      "",

    role:
      "worker",

    platformRole:
      "worker",

    tenantId:
      resolvedTenantId,

    status:
      "active",

    facilityId:
      resolvedFacilityId,

    primaryFacilityId:
      resolvedFacilityId,

    facilityIds:
      finalFacilityIds,

    allowedFacilityIds:
      finalFacilityIds,

    credentials:
      Array.isArray(
        latest?.credentials
      )
        ? latest.credentials
        : [],

    disabled:
      false,

    source:
      "compliance_worker_approval",
  });

  accessFinalized = true;
}


/* =====================================================
   AUDIT
   ===================================================== */

try {
  await addDoc(
    collection(
      db,
      "adminLogs"
    ),
    {
      action:
        "worker_approved",

      workerUid:
        worker.uid,

      workerEmail:
        worker.email || "",

      approvedBy,

      tenantId:
        resolvedTenantId,

      facilityId:
        resolvedFacilityId || "",

      facilityAssigned:
        hasFacilityAssignment,

      accessFinalized,

      createdAt:
        serverTimestamp(),
    }
  );
} catch (logError) {
  console.warn(
    "Worker approval log failed:",
    logError
  );
}


/* =====================================================
   USER FEEDBACK
   ===================================================== */

if (accessFinalized) {
  alert(
    "Worker approved. Facility membership and operational access are ready."
  );
} else {
  alert(
    "Worker approved. Facility assignment is still required before operational shift access is created."
  );
}
  } catch (err) {
    console.error("approveWorkerIfReady error:", err);
    alert(err?.message || "Unable to approve worker.");
  } finally {
    setBusy("");
  }
}

const ReviewPresentation = approvalOnly ? OnboardingReviewWorkspace : DepartmentLayout;
return (
  <ReviewPresentation
    header={
      <EnterpriseHeader
        title="Compliance Center"
        subtitle="Worker onboarding, document review, reuploads, expirations, and compliance."
      />
    }
  >
  {preview && <DocumentPreview key={`${preview.workerUid}-${preview.key}-${documentReviewVersion(preview.item)}`} item={preview.item} loadFile={loadDocumentFile} onClose={()=>setPreview(null)} onReviewed={()=>setReviewedDocs(previous=>({...previous,[`${preview.workerUid}-${preview.key}`]:documentReviewVersion(preview.item)}))} />}
  {reuploadRequest && <ReuploadRequestDialog label={reuploadRequest.label} busy={Boolean(busy)} onClose={()=>setReuploadRequest(null)} onSave={async reason=>{if(await reviewDocument(reuploadRequest.worker,reuploadRequest.key,'reupload_required',reason)) setReuploadRequest(null);}} />}
  {!approvalOnly && (<ComplianceSummary
  underReview={underReview.length}
  actionRequired={actionRequired.length}
  approved={approvedWorkers.length}
  expiring={expiringSoon.length}
  ready={approvedWorkers.length}
/>)}

    {!approvalOnly && (<OperationsCoach
      title="ShiftBridge Assist"
      subtitle="Today's compliance priorities"
      estimatedMinutes={Math.max(
        5,
        underReview.length * 5 + actionRequired.length * 3
      )}
      items={[
        ...(underReview.length
          ? [
              {
                label: `🟠 Review ${underReview.length} worker(s)`,
                bg: "#ffedd5",
                border: "#fdba74",
                color: "#9a3412",
              },
            ]
          : []),

        ...(actionRequired.length
          ? [
              {
                label: `🔴 ${actionRequired.length} worker(s) require action`,
                bg: "#fee2e2",
                border: "#fecaca",
                color: "#991b1b",
              },
            ]
          : []),

        ...(expiringSoon.length
          ? [
              {
                label: `🟡 ${expiringSoon.length} credential(s) expiring`,
                bg: "#fef3c7",
                border: "#fde68a",
                color: "#92400e",
              },
            ]
          : []),

        ...(approvedWorkers.length
          ? [
              {
                label: `🟢 ${approvedWorkers.length} workers ready`,
                bg: "#dcfce7",
                border: "#86efac",
                color: "#166534",
              },
            ]
          : []),
      ]}
    />)}

    {reviewError && <div role="alert">{reviewError}</div>}
    <ComplianceQueue>
      {reviewWorkers.length === 0 ? (
        <div style={{ color: "#64748b", fontWeight: 800 }}>
          No workers waiting for compliance review.
        </div>
      ) : (
        reviewWorkers.map((worker) => {

const docs =
  worker.credentialMap ||
  worker.documents ||
  worker.uploadedDocs ||
  {};

const docsApproved = REQUIRED_DOCUMENTS.filter(([key]) => {
  const item = docs?.[key] || {};

  return (
    String(item.status || "").toLowerCase() === "approved" &&
    item.verified === true
  );
}).length;

const allDocsApproved =
  docsApproved === REQUIRED_DOCUMENTS.length;

       return (
  <ComplianceWorkerCard
    key={worker.uid}
    worker={worker}
    docsApproved={docsApproved}
    allDocsApproved={allDocsApproved}
    busy={busy}
    onApproveAllDocs={(worker) => {
      const docs = worker.credentialMap || {};

      const missingDocs = REQUIRED_DOCUMENTS.filter(([key]) => {
        const item = docs?.[key] || {};
        return !item.fileUrl;
      });

      if (missingDocs.length > 0) {
        alert("Cannot approve all. Worker must upload all required documents first.");
        return;
      }

      const unreviewedDocs = REQUIRED_DOCUMENTS.filter(([key]) => {
        const item = docs?.[key] || {};
        const hasFile = item.fileUrl;
        return hasFile && reviewedDocs[`${worker.uid}-${key}`] !== documentReviewVersion(item);
      });

      if (unreviewedDocs.length > 0) {
        const firstUnreviewedDoc = REQUIRED_DOCUMENTS.find(([key]) => {
          const item = docs?.[key] || {};
          const uploaded = item.fileUrl;
          const reviewed = reviewedDocs[`${worker.uid}-${key}`] === documentReviewVersion(item);

          return uploaded && !reviewed;
        });

        if (firstUnreviewedDoc) {
          alert("Open and review each uploaded document before approving all documents.");
          scrollToDoc(worker.uid, firstUnreviewedDoc[0]);
          return;
        }

        return;
      }

      approveAllDocuments(worker);
    }}
    onApproveWorker={approveWorkerIfReady}
  >

              <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                {REQUIRED_DOCUMENTS.map(([key, label]) => {
                  const item = docs?.[key] || {};
                  const status = item.status || "missing";

                 return (
  <ComplianceDocumentRow
    key={key}
    id={`doc-${worker.uid}-${key}`}
    label={label}
    status={status}
    fileUrl={item.fileUrl}
    busy={Boolean(busy)}
    reviewed={reviewedDocs[`${worker.uid}-${key}`] === documentReviewVersion(item)}
    onOpenDocument={() => {
      setPreview({workerUid:worker.uid,key,item});
    }}
    onApprove={() => {
      if (!item.fileUrl) {
        alert("No uploaded file found. Worker must upload this document first.");
        return;
      }

      if (reviewedDocs[`${worker.uid}-${key}`] !== documentReviewVersion(item)) {
        alert("Open and review this document before approving it.");
        return;
      }

      reviewDocument(worker, key, "approved");
    }}
    onReupload={() => setReuploadRequest({worker,key,label})}
  />
); 
                })}
              </div>
            </ComplianceWorkerCard>
          );
        })
      )}
    </ComplianceQueue>
  </ReviewPresentation>
);
}
